import { useCallback, useEffect, useState } from 'react'
import { authFetch } from '../lib/authFetch'
import type { ProjectDetailResponse, TargetProfileResponse } from '../types'

export interface UseProjectDetailResult {
  data: ProjectDetailResponse | null
  targetProfile: TargetProfileResponse | null
  loading: boolean
  error: string | null
  /** Re-fetch full project detail from server */
  refetch: () => void
  /** Optimistically merge partial project updates into current data */
  mutateProject: (partial: Partial<ProjectDetailResponse['project']>) => void
}

export function useProjectDetail(projectId: string | null): UseProjectDetailResult {
  const [data, setData] = useState<ProjectDetailResponse | null>(null)
  const [targetProfile, setTargetProfile] = useState<TargetProfileResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(() => {
    if (!projectId) {
      setData(null)
      setTargetProfile(null)
      return
    }

    setLoading(true)
    setError(null)

    authFetch(`/api/v2/projects/${projectId}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json() as Promise<ProjectDetailResponse>
      })
      .then(projectData => {
        setData(projectData)

        if (projectData.project.domain === 'biotech') {
          return authFetch(`/api/v2/projects/${projectId}/target-profile`)
            .then(r => r.json() as Promise<TargetProfileResponse>)
            .then(tp => setTargetProfile(tp))
            .catch(e => {
              console.warn('Target profile fetch failed:', e)
            })
        }
      })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false))
  }, [projectId])

  useEffect(() => {
    setData(null)
    setTargetProfile(null)
    fetchData()
  }, [fetchData])

  const mutateProject = useCallback((partial: Partial<ProjectDetailResponse['project']>) => {
    setData(prev => {
      if (!prev) return prev
      return {
        ...prev,
        project: { ...prev.project, ...partial },
      }
    })
  }, [])

  return { data, targetProfile, loading, error, refetch: fetchData, mutateProject }
}
